import {
  buildFacetsMetaForRecords,
  cptCodeRateIdsForClinicianId,
  filterClinicians,
  officesForCptCodeRateAndClinicianId,
  paginateRecords,
  probeNameMatch,
} from 'client-portal/utils/clinicianSearch/send-all-catalog';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

export default class ClinicianSearchSendAllService extends Service {
  @service store;

  @tracked isLoading = false;
  @tracked loadError = null;
  @tracked _records = null;

  #catalogErrorCode = null;
  #loadPromise = null;

  get isLoaded() {
    return !!this._records;
  }

  get records() {
    return this._records ?? [];
  }

  resetState() {
    this._records = null;
    this.#catalogErrorCode = null;
    this.#loadPromise = null;
    this.loadError = null;
    this.isLoading = false;
  }

  async ensureLoaded() {
    if (this._records) {
      return this._records;
    }
    this.#loadPromise ??= this.#startLoad();
    return this.#loadPromise;
  }

  #startLoad() {
    this.isLoading = true;
    this.loadError = null;

    return (async () => {
      try {
        let records = await this.#fetchAll();
        this._records = records;
        return records;
      } catch (err) {
        this.loadError = err;
        throw err;
      } finally {
        this.isLoading = false;
        this.#loadPromise = null;
      }
    })();
  }

  // Send-all emits standard JSON:API of type `clinicians`, so Ember Data
  // normalizes natively. The `clinicianSearch: true` marker is consumed by
  // the clinician adapter's urlForQuery and stripped from the outgoing params.
  async #fetchAll() {
    let result = await this.store.query('clinician', { clinicianSearch: true });
    this.#catalogErrorCode = result.meta?.errorCode ?? null;
    await this.loadDependencies();
    return result.toArray();
  }

  async getClinicianIds() {
    let records = await this.records;
    return records.map(clinician => clinician.id);
  }

  async loadDependencies() {
    await this.store.query('office', {});
    await this.store.query('cpt-code', {});
  }

  filterForRouteParams(routeParams) {
    return filterClinicians(this.records, routeParams);
  }

  buildFacetsMeta(routeParams) {
    let cohort = filterClinicians(this.records, routeParams);
    return buildFacetsMetaForRecords(cohort, { routeParams });
  }

  probeName(routeParams, nameKey) {
    return probeNameMatch(this.records, routeParams, nameKey);
  }

  cptCodeRateIdsForClinician(clinicianId, { officeIds } = {}) {
    return cptCodeRateIdsForClinicianId(this.records, clinicianId, { officeIds });
  }

  officesForCptCodeRateAndClinicianId(cptCodeRateId, clinicianId) {
    return officesForCptCodeRateAndClinicianId(this.records, cptCodeRateId, clinicianId);
  }

  resolvePage(routeParams, pageSize, sortFn = null) {
    let filtered = this.filterForRouteParams(routeParams);
    let ordered = sortFn ? sortFn(filtered) : filtered;
    let { pageNumber, pageRecords, total } = paginateRecords(
      ordered,
      routeParams.pageNumber,
      pageSize
    );
    let meta = {
      page: { number: pageNumber, pageSize, total },
    };
    if (typeof this.#catalogErrorCode === 'number') {
      meta.errorCode = this.#catalogErrorCode;
    }

    // Records are already in the Ember Data store from #fetchAll; the page
    // is just a sliced view.
    return {
      clinicians: pageRecords,
      meta,
      pageNumber,
    };
  }
}
